import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useMicrosoftClarity } from '@/hooks/useMicrosoftClarity';
import { useGoogleAdSense } from '@/hooks/useGoogleAdSense';
import { AuthProvider } from '@/contexts/AuthContext';
import HomePage from '@/components/HomePage';
import PreviewPage from '@/components/PreviewPage';
import './App.css';

// Code splitting - lazy load components
const YouTubeDownloaderPage = React.lazy(() => import('@/components/YouTubeDownloaderPage'));
const TikTokDownloaderPage = React.lazy(() => import('@/components/TikTokDownloaderPage'));
const TwitterDownloaderPage = React.lazy(() => import('@/components/TwitterDownloaderPage'));
const FacebookDownloaderPage = React.lazy(() => import('@/components/FacebookDownloaderPage'));
const InstagramDownloaderPage = React.lazy(() => import('@/components/InstagramDownloaderPage'));
const VimeoDownloaderPage = React.lazy(() => import('@/components/VimeoDownloaderPage'));
const BilibiliDownloaderPage = React.lazy(() => import('@/components/BilibiliDownloaderPage'));
const XiaohongshuDownloaderPage = React.lazy(() => import('@/components/XiaohongshuDownloaderPage'));
const ApplePodcastsDownloaderPage = React.lazy(() => import('@/components/ApplePodcastsDownloaderPage'));
const SoundCloudDownloaderPage = React.lazy(() => import('@/components/SoundCloudDownloaderPage'));
const TuneInDownloaderPage = React.lazy(() => import('@/components/TuneInDownloaderPage'));
const PlayerFMDownloaderPage = React.lazy(() => import('@/components/PlayerFMDownloaderPage'));
const CastboxDownloaderPage = React.lazy(() => import('@/components/CastboxDownloaderPage'));
const XiaoyuzhouDownloaderPage = React.lazy(() => import('@/components/XiaoyuzhouDownloaderPage'));
const HowToDownloadVideosPage = React.lazy(() => import('@/components/HowToDownloadVideosPage'));
const BlogPage = React.lazy(() => import('@/components/BlogPage'));
const BlogPostPage = React.lazy(() => import('@/components/BlogPostPage'));
const HelpCenterPage = React.lazy(() => import('@/components/HelpCenterPage'));
const CookieSettingsPage = React.lazy(() => import('@/components/CookieSettingsPage'));
const PrivacyPolicyPage = React.lazy(() => import('@/components/PrivacyPolicyPage'));
const DMCAPage = React.lazy(() => import('@/components/DMCAPage'));
const TermsOfServicePage = React.lazy(() => import('@/components/TermsOfServicePage'));
const TermsGlossaryPage = React.lazy(() => import('@/components/TermsGlossaryPage'));
const NetworkPage = React.lazy(() => import('@/components/NetworkPage'));
const ShortUrlRedirectFallbackPage = React.lazy(() => import('@/components/ShortUrlRedirectFallbackPage'));

// Loading component for code splitting
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#FF6B35' }}></div>
  </div>
);

function AppContent() {
  const config = useDomainConfig();
  const { trackPageView } = useGoogleAnalytics();
  const { trackPageView: trackClarityPageView } = useMicrosoftClarity();

  // Initialize Google AdSense
  useGoogleAdSense();

  // Apply domain-specific CSS variables
  React.useEffect(() => {
    const root = document.documentElement;
    const { colors } = config.branding;

    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-text-primary', colors.text.primary);
    root.style.setProperty('--color-text-secondary', colors.text.secondary);
    root.style.setProperty('--color-text-muted', colors.text.muted);

    // Update page title and meta tags (but not for pages that manage their own titles)
    if (window.location.pathname !== '/preview' && !window.location.pathname.startsWith('/p/')) {
      document.title = config.seo.title;
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', config.seo.description);
    }

    // Update favicon if specified
    if (config.branding.favicon) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = config.branding.favicon;
      }
    }
  }, [config]);

  // Track page views for Google Analytics and Microsoft Clarity
  React.useEffect(() => {
    const handleRouteChange = () => {
      trackPageView(window.location.pathname, document.title);
      trackClarityPageView(window.location.pathname, document.title);
    };

    // Track initial page load
    handleRouteChange();

    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [trackPageView, trackClarityPageView]);

  return (
    <div className="min-h-screen bg-background">
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/preview" element={<PreviewPage />} />
          {/* Short URL redirect handler for expired/missing links */}
          <Route path="/p/:shortId" element={<ShortUrlRedirectFallbackPage />} />
          {/* Platform-specific routes - YouTube (Multiple SEO-friendly aliases) */}
          <Route path="/youtube-downloader" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-video-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-mp4-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-mp3-download" element={<YouTubeDownloaderPage />} />
          <Route path="/download-youtube-video" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-to-mp4" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-to-mp3" element={<YouTubeDownloaderPage />} />
          <Route path="/yt-download" element={<YouTubeDownloaderPage />} />
          <Route path="/yt-downloader" element={<YouTubeDownloaderPage />} />
          {/* Quality-specific YouTube download routes */}
          <Route path="/youtube-4k-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-1080p-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-720p-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-480p-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-hd-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-full-hd-download" element={<YouTubeDownloaderPage />} />
          <Route path="/youtube-uhd-download" element={<YouTubeDownloaderPage />} />
          {/* TikTok-specific routes with dedicated page */}
          <Route path="/tiktok-downloader" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-video-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-video" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-video-downloader" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-mp4-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-mp3-download" element={<TikTokDownloaderPage />} />
          <Route path="/download-tiktok-video" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-to-mp4" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-to-mp3" element={<TikTokDownloaderPage />} />
          <Route path="/tt-download" element={<TikTokDownloaderPage />} />
          <Route path="/tt-downloader" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-hd-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-no-watermark" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-watermark-free" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-audio-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-sound-download" element={<TikTokDownloaderPage />} />
          <Route path="/download-tiktok-audio" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-music-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-song-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-without-watermark" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-video-saver" element={<TikTokDownloaderPage />} />
          <Route path="/save-tiktok-video" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-clip-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-reel-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-content-download" element={<TikTokDownloaderPage />} />
          <Route path="/tiktok-media-download" element={<TikTokDownloaderPage />} />
          <Route path="/free-tiktok-downloader" element={<TikTokDownloaderPage />} />
          <Route path="/online-tiktok-downloader" element={<TikTokDownloaderPage />} />
          {/* Twitter/X-specific routes with dedicated page */}
          <Route path="/twitter-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-download" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-video-download" element={<TwitterDownloaderPage />} />
          <Route path="/twitter" element={<TwitterDownloaderPage />} />
          <Route path="/x-download" element={<TwitterDownloaderPage />} />
          <Route path="/x-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/x-video-download" element={<TwitterDownloaderPage />} />
          <Route path="/download-twitter-video" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-to-mp4" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-to-mp3" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-video-saver" element={<TwitterDownloaderPage />} />
          <Route path="/save-twitter-video" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-media-download" element={<TwitterDownloaderPage />} />
          <Route path="/x-media-download" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-gif-download" element={<TwitterDownloaderPage />} />
          <Route path="/x-gif-download" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-image-download" element={<TwitterDownloaderPage />} />
          <Route path="/x-image-download" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-hd-download" element={<TwitterDownloaderPage />} />
          <Route path="/x-hd-download" element={<TwitterDownloaderPage />} />
          <Route path="/twitter-video-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/x-video-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/free-twitter-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/online-twitter-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/free-x-downloader" element={<TwitterDownloaderPage />} />
          <Route path="/online-x-downloader" element={<TwitterDownloaderPage />} />
          {/* Facebook-specific routes with dedicated page */}
          <Route path="/facebook-downloader" element={<FacebookDownloaderPage />} />
          <Route path="/facebook-download" element={<FacebookDownloaderPage />} />
          <Route path="/facebook-video-download" element={<FacebookDownloaderPage />} />
          <Route path="/facebook" element={<FacebookDownloaderPage />} />
          <Route path="/fb-download" element={<FacebookDownloaderPage />} />
          <Route path="/fb-downloader" element={<FacebookDownloaderPage />} />
          <Route path="/download-facebook-video" element={<FacebookDownloaderPage />} />
          <Route path="/facebook-to-mp4" element={<FacebookDownloaderPage />} />
          <Route path="/facebook-video-saver" element={<FacebookDownloaderPage />} />
          <Route path="/save-facebook-video" element={<FacebookDownloaderPage />} />
          <Route path="/facebook-media-download" element={<FacebookDownloaderPage />} />
          {/* Instagram-specific routes with dedicated page */}
          <Route path="/instagram-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-video-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-mp4-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-mp3-download" element={<InstagramDownloaderPage />} />
          <Route path="/download-instagram-video" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-to-mp4" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-to-mp3" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-hd-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-4k-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-1080p-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-720p-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-480p-download" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-video-saver" element={<InstagramDownloaderPage />} />
          <Route path="/save-instagram-video" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-media-download" element={<InstagramDownloaderPage />} />
          <Route path="/free-instagram-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/online-instagram-downloader" element={<InstagramDownloaderPage />} />
          {/* Instagram Reels specific routes */}
          <Route path="/instagram-reels-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-reels-download" element={<InstagramDownloaderPage />} />
          <Route path="/download-instagram-reels" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-reel-download" element={<InstagramDownloaderPage />} />
          <Route path="/ig-reels-download" element={<InstagramDownloaderPage />} />
          <Route path="/save-instagram-reels" element={<InstagramDownloaderPage />} />
          {/* Instagram Stories specific routes */}
          <Route path="/instagram-stories-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-stories-download" element={<InstagramDownloaderPage />} />
          <Route path="/download-instagram-stories" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-story-download" element={<InstagramDownloaderPage />} />
          <Route path="/ig-stories-download" element={<InstagramDownloaderPage />} />
          <Route path="/save-instagram-stories" element={<InstagramDownloaderPage />} />
          {/* Instagram IGTV specific routes */}
          <Route path="/instagram-igtv-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/instagram-igtv-download" element={<InstagramDownloaderPage />} />
          <Route path="/download-instagram-igtv" element={<InstagramDownloaderPage />} />
          <Route path="/igtv-download" element={<InstagramDownloaderPage />} />
          <Route path="/ig-tv-download" element={<InstagramDownloaderPage />} />
          <Route path="/save-instagram-igtv" element={<InstagramDownloaderPage />} />
          {/* IG short aliases */}
          <Route path="/ig-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/ig-download" element={<InstagramDownloaderPage />} />
          <Route path="/ig-video-download" element={<InstagramDownloaderPage />} />
          <Route path="/download-ig-video" element={<InstagramDownloaderPage />} />
          <Route path="/ig-to-mp4" element={<InstagramDownloaderPage />} />
          <Route path="/ig-video-saver" element={<InstagramDownloaderPage />} />
          <Route path="/save-ig-video" element={<InstagramDownloaderPage />} />
          <Route path="/ig-media-download" element={<InstagramDownloaderPage />} />
          <Route path="/free-ig-downloader" element={<InstagramDownloaderPage />} />
          <Route path="/online-ig-downloader" element={<InstagramDownloaderPage />} />
          {/* Vimeo-specific routes with dedicated page */}
          <Route path="/vimeo-downloader" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-video-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-mp4-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-mp3-download" element={<VimeoDownloaderPage />} />
          <Route path="/download-vimeo-video" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-to-mp4" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-to-mp3" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-hd-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-4k-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-1080p-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-720p-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-480p-download" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-video-saver" element={<VimeoDownloaderPage />} />
          <Route path="/save-vimeo-video" element={<VimeoDownloaderPage />} />
          <Route path="/vimeo-media-download" element={<VimeoDownloaderPage />} />
          <Route path="/free-vimeo-downloader" element={<VimeoDownloaderPage />} />
          <Route path="/online-vimeo-downloader" element={<VimeoDownloaderPage />} />
          {/* Bilibili-specific routes with dedicated page */}
          <Route path="/bilibili-downloader" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-video-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-mp4-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-mp3-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-to-mp4" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-to-mp3" element={<BilibiliDownloaderPage />} />
          <Route path="/download-bilibili-video" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-hd-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-4k-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-1080p-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-720p-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-480p-download" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-video-saver" element={<BilibiliDownloaderPage />} />
          <Route path="/save-bilibili-video" element={<BilibiliDownloaderPage />} />
          <Route path="/bilibili-media-download" element={<BilibiliDownloaderPage />} />
          <Route path="/free-bilibili-downloader" element={<BilibiliDownloaderPage />} />
          <Route path="/online-bilibili-downloader" element={<BilibiliDownloaderPage />} />
          {/* Xiaohongshu/RedNote-specific routes with dedicated page */}
          <Route path="/xiaohongshu-downloader" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-video-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-mp4-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-mp3-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-to-mp4" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-to-mp3" element={<XiaohongshuDownloaderPage />} />
          <Route path="/download-xiaohongshu-video" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-hd-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-4k-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-1080p-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-720p-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-480p-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-video-saver" element={<XiaohongshuDownloaderPage />} />
          <Route path="/save-xiaohongshu-video" element={<XiaohongshuDownloaderPage />} />
          <Route path="/xiaohongshu-media-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/free-xiaohongshu-downloader" element={<XiaohongshuDownloaderPage />} />
          <Route path="/online-xiaohongshu-downloader" element={<XiaohongshuDownloaderPage />} />
          {/* RedNote alternative routes */}
          <Route path="/rednote-downloader" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/download-rednote-video" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-video-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-mp4-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-mp3-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-to-mp4" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-to-mp3" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-hd-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-4k-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-1080p-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-720p-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-480p-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-video-saver" element={<XiaohongshuDownloaderPage />} />
          <Route path="/save-rednote-video" element={<XiaohongshuDownloaderPage />} />
          <Route path="/rednote-media-download" element={<XiaohongshuDownloaderPage />} />
          <Route path="/free-rednote-downloader" element={<XiaohongshuDownloaderPage />} />
          <Route path="/online-rednote-downloader" element={<XiaohongshuDownloaderPage />} />
          {/* Apple Podcasts-specific routes with dedicated page */}
          <Route path="/apple-podcasts-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts-mp3-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts-to-mp3" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/download-apple-podcasts" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts-audio-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts-episode-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/itunes-podcast-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcast-saver" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/save-apple-podcast" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/apple-podcasts-media-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/free-apple-podcasts-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/online-apple-podcasts-downloader" element={<ApplePodcastsDownloaderPage />} />

          {/* General Podcast routes (broader SEO coverage) - all use ApplePodcastsDownloaderPage */}
          <Route path="/podcast-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/free-podcast-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/free-podcasts-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/online-podcast-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/online-podcasts-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-mp3-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-mp3-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-audio-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-audio-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/download-podcast" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/download-podcasts" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-episode-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-episode-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-to-mp3" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-to-mp3" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-converter" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-converter" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-saver" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-saver" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/save-podcast" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/save-podcasts" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-media-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-media-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/free-podcast-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/free-podcasts-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/online-podcast-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/online-podcasts-downloader" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-offline-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-offline-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcast-high-quality-download" element={<ApplePodcastsDownloaderPage />} />
          <Route path="/podcasts-high-quality-download" element={<ApplePodcastsDownloaderPage />} />

          {/* SoundCloud-specific routes with dedicated page */}
          <Route path="/soundcloud-downloader" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-mp3-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-to-mp3" element={<SoundCloudDownloaderPage />} />
          <Route path="/download-soundcloud" element={<SoundCloudDownloaderPage />} />
          <Route path="/download-soundcloud-track" element={<SoundCloudDownloaderPage />} />
          <Route path="/download-soundcloud-song" element={<SoundCloudDownloaderPage />} />
          <Route path="/download-soundcloud-music" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-audio-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-track-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-song-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-music-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-playlist-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-converter" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-to-audio" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-saver" element={<SoundCloudDownloaderPage />} />
          <Route path="/save-soundcloud-track" element={<SoundCloudDownloaderPage />} />
          <Route path="/save-soundcloud-song" element={<SoundCloudDownloaderPage />} />
          <Route path="/save-soundcloud-music" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-media-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/free-soundcloud-downloader" element={<SoundCloudDownloaderPage />} />
          <Route path="/online-soundcloud-downloader" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-320kbps-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-hq-download" element={<SoundCloudDownloaderPage />} />
          <Route path="/soundcloud-high-quality-download" element={<SoundCloudDownloaderPage />} />

          {/* TuneIn-specific routes with dedicated page */}
          <Route path="/tunein-downloader" element={<TuneInDownloaderPage />} />
          <Route path="/tunein" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-podcast-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-mp3-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-to-mp3" element={<TuneInDownloaderPage />} />
          <Route path="/download-tunein" element={<TuneInDownloaderPage />} />
          <Route path="/download-tunein-podcast" element={<TuneInDownloaderPage />} />
          <Route path="/download-tunein-radio" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-audio-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-episode-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-radio-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-podcast-downloader" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-radio-downloader" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-show-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-converter" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-to-audio" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-saver" element={<TuneInDownloaderPage />} />
          <Route path="/save-tunein-podcast" element={<TuneInDownloaderPage />} />
          <Route path="/save-tunein-radio" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-media-download" element={<TuneInDownloaderPage />} />
          <Route path="/free-tunein-downloader" element={<TuneInDownloaderPage />} />
          <Route path="/online-tunein-downloader" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-offline-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-high-quality-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-320kbps-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-hq-download" element={<TuneInDownloaderPage />} />

          {/* TuneIn Radio specific routes */}
          <Route path="/tunein-radio" element={<TuneInDownloaderPage />} />
          <Route path="/radio-tunein-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-live-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-stream-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-station-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-talk-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-news-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-sports-download" element={<TuneInDownloaderPage />} />
          <Route path="/tunein-music-download" element={<TuneInDownloaderPage />} />

          {/* Player.FM-specific routes with dedicated page */}
          <Route path="/playerfm-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-podcast-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-mp3-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-to-mp3" element={<PlayerFMDownloaderPage />} />
          <Route path="/download-playerfm" element={<PlayerFMDownloaderPage />} />
          <Route path="/download-playerfm-podcast" element={<PlayerFMDownloaderPage />} />
          <Route path="/download-playerfm-episode" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-audio-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-episode-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-podcast-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-episode-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-show-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-converter" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-to-audio" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-saver" element={<PlayerFMDownloaderPage />} />
          <Route path="/save-playerfm-podcast" element={<PlayerFMDownloaderPage />} />
          <Route path="/save-playerfm-episode" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-media-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/free-playerfm-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/online-playerfm-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-offline-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-high-quality-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-320kbps-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/playerfm-hq-download" element={<PlayerFMDownloaderPage />} />

          {/* Player.FM alternative routes (with hyphen) */}
          <Route path="/player-fm-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/player-fm" element={<PlayerFMDownloaderPage />} />
          <Route path="/player-fm-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/player-fm-podcast-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/download-player-fm" element={<PlayerFMDownloaderPage />} />
          <Route path="/download-player-fm-podcast" element={<PlayerFMDownloaderPage />} />
          <Route path="/player-fm-audio-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/player-fm-episode-download" element={<PlayerFMDownloaderPage />} />
          <Route path="/free-player-fm-downloader" element={<PlayerFMDownloaderPage />} />
          <Route path="/online-player-fm-downloader" element={<PlayerFMDownloaderPage />} />

          {/* Castbox-specific routes with dedicated page */}
          <Route path="/castbox-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/castbox" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-podcast-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-mp3-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-to-mp3" element={<CastboxDownloaderPage />} />
          <Route path="/download-castbox" element={<CastboxDownloaderPage />} />
          <Route path="/download-castbox-podcast" element={<CastboxDownloaderPage />} />
          <Route path="/download-castbox-episode" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-audio-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-episode-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-podcast-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-episode-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-show-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-converter" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-to-audio" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-saver" element={<CastboxDownloaderPage />} />
          <Route path="/save-castbox-podcast" element={<CastboxDownloaderPage />} />
          <Route path="/save-castbox-episode" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-media-download" element={<CastboxDownloaderPage />} />
          <Route path="/free-castbox-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/online-castbox-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-offline-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-high-quality-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-320kbps-download" element={<CastboxDownloaderPage />} />
          <Route path="/castbox-hq-download" element={<CastboxDownloaderPage />} />

          {/* Castbox.fm alternative routes */}
          <Route path="/castboxfm-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/castboxfm" element={<CastboxDownloaderPage />} />
          <Route path="/castboxfm-download" element={<CastboxDownloaderPage />} />
          <Route path="/castboxfm-podcast-download" element={<CastboxDownloaderPage />} />
          <Route path="/download-castboxfm" element={<CastboxDownloaderPage />} />
          <Route path="/download-castboxfm-podcast" element={<CastboxDownloaderPage />} />
          <Route path="/castboxfm-audio-download" element={<CastboxDownloaderPage />} />
          <Route path="/castboxfm-episode-download" element={<CastboxDownloaderPage />} />
          <Route path="/free-castboxfm-downloader" element={<CastboxDownloaderPage />} />
          <Route path="/online-castboxfm-downloader" element={<CastboxDownloaderPage />} />

          {/* Xiaoyuzhou FM-specific routes with dedicated page */}
          <Route path="/xiaoyuzhou-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhoufm-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhoufm" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-podcast-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-mp3-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-to-mp3" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/download-xiaoyuzhou" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/download-xiaoyuzhou-podcast" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/download-xiaoyuzhou-episode" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-audio-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-episode-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-podcast-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-episode-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-show-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-converter" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-to-audio" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-saver" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/save-xiaoyuzhou-podcast" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/save-xiaoyuzhou-episode" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-media-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/free-xiaoyuzhou-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/online-xiaoyuzhou-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-offline-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-high-quality-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-320kbps-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhou-hq-download" element={<XiaoyuzhouDownloaderPage />} />

          {/* XiaoyuzhouFM alternative routes */}
          <Route path="/xiaoyuzhoufm-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhoufm-podcast-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/download-xiaoyuzhoufm" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/download-xiaoyuzhoufm-podcast" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhoufm-audio-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/xiaoyuzhoufm-episode-download" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/free-xiaoyuzhoufm-downloader" element={<XiaoyuzhouDownloaderPage />} />
          <Route path="/online-xiaoyuzhoufm-downloader" element={<XiaoyuzhouDownloaderPage />} />

          {/* How-To Guide routes - comprehensive SEO coverage */}
          <Route path="/how-to-download-videos" element={<HowToDownloadVideosPage />} />
          <Route path="/how-to-download-videos.html" element={<HowToDownloadVideosPage />} />
          <Route path="/video-download-guide" element={<HowToDownloadVideosPage />} />
          <Route path="/download-guide" element={<HowToDownloadVideosPage />} />
          <Route path="/video-downloading-tutorial" element={<HowToDownloadVideosPage />} />
          <Route path="/how-to-save-videos" element={<HowToDownloadVideosPage />} />
          <Route path="/video-downloader-guide" element={<HowToDownloadVideosPage />} />
          <Route path="/complete-download-guide" element={<HowToDownloadVideosPage />} />
          <Route path="/video-formats-guide" element={<HowToDownloadVideosPage />} />
          <Route path="/download-tutorial" element={<HowToDownloadVideosPage />} />

          {/* Blog routes - content strategy for AdSense */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPostPage />} />
          <Route path="/blog.html" element={<BlogPage />} />
          <Route path="/articles" element={<BlogPage />} />
          <Route path="/guides" element={<BlogPage />} />
          <Route path="/tutorials" element={<BlogPage />} />
          <Route path="/video-download-tips" element={<BlogPage />} />
          <Route path="/content" element={<BlogPage />} />
          <Route path="/educational-content" element={<BlogPage />} />
          <Route path="/video-format-guides" element={<BlogPage />} />
          <Route path="/platform-comparisons" element={<BlogPage />} />
          <Route path="/legal-guides" element={<BlogPage />} />
          <Route path="/copyright-information" element={<BlogPage />} />
          <Route path="/download-best-practices" element={<BlogPage />} />
          <Route path="/quality-optimization-guides" element={<BlogPage />} />

          {/* Help Center routes */}
          <Route path="/help-center" element={<HelpCenterPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/support" element={<HelpCenterPage />} />
          <Route path="/faq-help" element={<HelpCenterPage />} />
          <Route path="/customer-support" element={<HelpCenterPage />} />

          {/* Cookie Settings routes */}
          <Route path="/cookie-settings" element={<CookieSettingsPage />} />
          <Route path="/cookies" element={<CookieSettingsPage />} />
          <Route path="/privacy-settings" element={<CookieSettingsPage />} />
          <Route path="/cookie-preferences" element={<CookieSettingsPage />} />
          <Route path="/privacy-preferences" element={<CookieSettingsPage />} />

          {/* Privacy Policy routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy.html" element={<PrivacyPolicyPage />} />
          <Route path="/data-privacy" element={<PrivacyPolicyPage />} />
          <Route path="/data-protection" element={<PrivacyPolicyPage />} />

          {/* DMCA Policy routes */}
          <Route path="/dmca-policy" element={<DMCAPage />} />
          <Route path="/dmca" element={<DMCAPage />} />
          <Route path="/dmca-policy.html" element={<DMCAPage />} />
          <Route path="/copyright-policy" element={<DMCAPage />} />
          <Route path="/copyright" element={<DMCAPage />} />
          <Route path="/takedown-policy" element={<DMCAPage />} />
          <Route path="/copyright-infringement" element={<DMCAPage />} />
          <Route path="/dmca-takedown" element={<DMCAPage />} />
          <Route path="/copyright-notice" element={<DMCAPage />} />
          <Route path="/intellectual-property" element={<DMCAPage />} />

          {/* Terms of Service routes */}
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/terms-of-service.html" element={<TermsOfServicePage />} />
          <Route path="/user-agreement" element={<TermsOfServicePage />} />
          <Route path="/service-terms" element={<TermsOfServicePage />} />
          <Route path="/legal-terms" element={<TermsOfServicePage />} />
          <Route path="/terms-and-conditions" element={<TermsOfServicePage />} />
          <Route path="/service-agreement" element={<TermsOfServicePage />} />
          <Route path="/usage-terms" element={<TermsOfServicePage />} />
          <Route path="/user-terms" element={<TermsOfServicePage />} />

          {/* Terms Glossary routes */}
          <Route path="/terms-glossary" element={<TermsGlossaryPage />} />
          <Route path="/glossary" element={<TermsGlossaryPage />} />
          <Route path="/definitions" element={<TermsGlossaryPage />} />
          <Route path="/terms-definitions" element={<TermsGlossaryPage />} />
          <Route path="/legal-glossary" element={<TermsGlossaryPage />} />

          {/* Network Hub Page routes */}
          <Route path="/network" element={<NetworkPage />} />
          <Route path="/alldl-network" element={<NetworkPage />} />
          <Route path="/our-network" element={<NetworkPage />} />
          <Route path="/download-network" element={<NetworkPage />} />
          <Route path="/video-download-network" element={<NetworkPage />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppContent />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
